/**
 * Tokens de apariencia claro/oscuro para toda la app.
 * Hoy solo los consumen Settings y ProfilePhotoUploadModal; el resto puede ir adoptando `getAppearanceTokens` progresivamente.
 */

export type AppTheme = 'light' | 'dark';

export interface SettingsScreenPalette {
  readonly pageBackground: string;
  readonly title: string;
  readonly cardBackground: string;
  readonly cardBorder: string;
  readonly cardShadowColor: string;
  readonly cardShadowOpacity: number;
  readonly cardHeaderTitle: string;
  readonly divider: string;
  readonly cardHint: string;
  readonly profileSeparator: string;
  readonly avatarPlaceholder: string;
  readonly avatarFallback: string;
  readonly avatarLetter: string;
  readonly photoButtonBorder: string;
  readonly photoButtonBackground: string;
  readonly photoButtonText: string;
  readonly readOnlyLabel: string;
  readonly readOnlyBoxBackground: string;
  readonly readOnlyBoxBorder: string;
  readonly readOnlyValue: string;
  readonly sectionLabel: string;
  readonly selectorBorder: string;
  readonly selectorBackground: string;
  readonly selectorValue: string;
  readonly selectorChevron: string;
  readonly selectorFocusBorder: string;
  readonly selectorOptionsBackground: string;
  readonly selectorOptionsBorder: string;
  readonly selectorOptionDivider: string;
  readonly selectorOptionText: string;
}

export interface ProfilePhotoModalPalette {
  readonly overlay: string;
  readonly sheetBackground: string;
  readonly sheetBorder: string;
  readonly shadowColor: string;
  readonly title: string;
  readonly subtitle: string;
  readonly guidelinesBackground: string;
  readonly guidelinesBorder: string;
  readonly guidelinesTitle: string;
  readonly guidelinesLine: string;
  readonly chooseButton: string;
  readonly chooseButtonText: string;
  readonly activityOnChoose: string;
  readonly previewRing: string;
  readonly previewMeta: string;
  readonly errorText: string;
  readonly successText: string;
  readonly footerBorder: string;
  readonly cancelBorder: string;
  readonly cancelBackground: string;
  readonly cancelText: string;
  readonly acceptBackground: string;
  readonly acceptText: string;
  readonly doneBackground: string;
  readonly doneText: string;
}

export interface HeaderPalette {
  readonly background: string;
  readonly border: string;
}

export interface TopBarProfilePalette {
  readonly triggerText: string;
  readonly triggerChevron: string;
  readonly avatarFallbackBackground: string;
  readonly avatarFallbackText: string;
  readonly dropdownBackground: string;
  readonly dropdownBorder: string;
  readonly menuDivider: string;
  readonly menuItemText: string;
  readonly menuItemValue: string;
  readonly submenuBackground: string;
  readonly submenuBorder: string;
  readonly submenuText: string;
  readonly logoutText: string;
}

export interface SidebarPalette {
  readonly overlay: string;
  readonly background: string;
  readonly shadowColor: string;
  readonly logoTitle: string;
  readonly collapseButtonBackground: string;
  readonly collapseIcon: string;
  readonly menuItemActiveBackground: string;
  readonly menuLabel: string;
  readonly menuLabelActive: string;
}

export interface HomePalette {
  readonly pageBackground: string;
  readonly headerBackground: string;
  readonly greeting: string;
  readonly welcomeText: string;
  readonly cardBackground: string;
  readonly cardBorder: string;
  readonly cardShadowColor: string;
  readonly cardShadowOpacity: number;
  readonly sectionTitle: string;
  readonly sectionArrow: string;
  readonly checkboxLabel: string;
  readonly textSecondary: string;
  readonly textMuted: string;
  readonly separator: string;
  readonly iconBackground: string;
  readonly iconForeground: string;
  readonly tableHeaderBackground: string;
  readonly tableRowAlternate: string;
  readonly exposureBarTrack: string;
}

export interface AppearanceTokens {
  readonly settings: SettingsScreenPalette;
  readonly modalProfilePhoto: ProfilePhotoModalPalette;
  readonly header: HeaderPalette;
  readonly topBarProfile: TopBarProfilePalette;
  readonly sidebar: SidebarPalette;
  readonly home: HomePalette;
}

const SETTINGS_LIGHT: SettingsScreenPalette = {
  pageBackground: '#F9FAFB',
  title: '#111827',
  cardBackground: '#FFFFFF',
  cardBorder: '#E5E7EB',
  cardShadowColor: '#000000',
  cardShadowOpacity: 0.06,
  cardHeaderTitle: '#111827',
  divider: '#E5E7EB',
  cardHint: '#6B7280',
  profileSeparator: '#F3F4F6',
  avatarPlaceholder: '#E5E7EB',
  avatarFallback: '#D1D5DB',
  avatarLetter: '#4B5563',
  photoButtonBorder: '#2563EB',
  photoButtonBackground: '#EFF6FF',
  photoButtonText: '#1D4ED8',
  readOnlyLabel: '#6B7280',
  readOnlyBoxBackground: '#F9FAFB',
  readOnlyBoxBorder: '#E5E7EB',
  readOnlyValue: '#374151',
  sectionLabel: '#374151',
  selectorBorder: '#D1D5DB',
  selectorBackground: '#FFFFFF',
  selectorValue: '#111827',
  selectorChevron: '#6B7280',
  selectorFocusBorder: '#2563EB',
  selectorOptionsBackground: '#FFFFFF',
  selectorOptionsBorder: '#E5E7EB',
  selectorOptionDivider: '#F3F4F6',
  selectorOptionText: '#374151',
};

const SETTINGS_DARK: SettingsScreenPalette = {
  pageBackground: '#0f172a',
  title: '#f1f5f9',
  cardBackground: '#1e293b',
  cardBorder: 'rgba(255,255,255,0.05)',
  cardShadowColor: '#000000',
  cardShadowOpacity: 0.4,
  cardHeaderTitle: '#F1F5F9',
  divider: 'rgba(255,255,255,0.06)',
  cardHint: '#94a3b8',
  profileSeparator: 'rgba(255,255,255,0.06)',
  avatarPlaceholder: '#334155',
  avatarFallback: '#475569',
  avatarLetter: '#f1f5f9',
  photoButtonBorder: '#3B82F6',
  photoButtonBackground: '#1d4ed8',
  photoButtonText: '#FFFFFF',
  readOnlyLabel: '#64748b',
  readOnlyBoxBackground: '#0b1220',
  readOnlyBoxBorder: 'rgba(255,255,255,0.08)',
  readOnlyValue: '#e5e7eb',
  sectionLabel: '#f1f5f9',
  selectorBorder: 'rgba(255,255,255,0.08)',
  selectorBackground: '#0b1220',
  selectorValue: '#e5e7eb',
  selectorChevron: '#94a3b8',
  selectorFocusBorder: '#3b82f6',
  selectorOptionsBackground: '#1e293b',
  selectorOptionsBorder: 'rgba(255,255,255,0.06)',
  selectorOptionDivider: 'rgba(255,255,255,0.06)',
  selectorOptionText: '#f1f5f9',
};

const MODAL_LIGHT: ProfilePhotoModalPalette = {
  overlay: 'rgba(17, 24, 39, 0.45)',
  sheetBackground: '#FFFFFF',
  sheetBorder: '#E5E7EB',
  shadowColor: '#000000',
  title: '#111827',
  subtitle: '#6B7280',
  guidelinesBackground: '#F9FAFB',
  guidelinesBorder: '#E5E7EB',
  guidelinesTitle: '#374151',
  guidelinesLine: '#6B7280',
  chooseButton: '#2563EB',
  chooseButtonText: '#FFFFFF',
  activityOnChoose: '#FFFFFF',
  previewRing: '#E5E7EB',
  previewMeta: '#6B7280',
  errorText: '#B91C1C',
  successText: '#047857',
  footerBorder: '#E5E7EB',
  cancelBorder: '#D1D5DB',
  cancelBackground: '#FFFFFF',
  cancelText: '#374151',
  acceptBackground: '#111827',
  acceptText: '#FFFFFF',
  doneBackground: '#111827',
  doneText: '#FFFFFF',
};

const HEADER_LIGHT: HeaderPalette = {
  background: '#FFFFFF',
  border: '#E5E7EB',
};

const HEADER_DARK: HeaderPalette = {
  background: '#1e293b',
  border: 'rgba(255,255,255,0.06)',
};

const TOP_BAR_PROFILE_LIGHT: TopBarProfilePalette = {
  triggerText: '#111827',
  triggerChevron: '#6B7280',
  avatarFallbackBackground: '#D1D5DB',
  avatarFallbackText: '#4B5563',
  dropdownBackground: '#FFFFFF',
  dropdownBorder: '#E5E7EB',
  menuDivider: '#E5E7EB',
  menuItemText: '#374151',
  menuItemValue: '#6B7280',
  submenuBackground: '#F9FAFB',
  submenuBorder: '#E5E7EB',
  submenuText: '#374151',
  logoutText: '#DC2626',
};

const TOP_BAR_PROFILE_DARK: TopBarProfilePalette = {
  triggerText: '#f1f5f9',
  triggerChevron: '#94a3b8',
  avatarFallbackBackground: '#334155',
  avatarFallbackText: '#f1f5f9',
  dropdownBackground: '#1e293b',
  dropdownBorder: 'rgba(255,255,255,0.08)',
  menuDivider: 'rgba(255,255,255,0.06)',
  menuItemText: '#e5e7eb',
  menuItemValue: '#94a3b8',
  submenuBackground: '#0b1220',
  submenuBorder: 'rgba(255,255,255,0.08)',
  submenuText: '#e5e7eb',
  logoutText: '#fca5a5',
};

const SIDEBAR_LIGHT: SidebarPalette = {
  overlay: 'rgba(0, 0, 0, 0.5)',
  background: '#FFFFFF',
  shadowColor: '#000000',
  logoTitle: '#1F2937',
  collapseButtonBackground: '#F3F4F6',
  collapseIcon: '#6B7280',
  menuItemActiveBackground: '#F0F9FF',
  menuLabel: '#9CA3AF',
  menuLabelActive: '#1F2937',
};

const SIDEBAR_DARK: SidebarPalette = {
  overlay: 'rgba(2, 6, 23, 0.7)',
  background: '#1e293b',
  shadowColor: '#000000',
  logoTitle: '#f1f5f9',
  collapseButtonBackground: '#0b1220',
  collapseIcon: '#94a3b8',
  menuItemActiveBackground: '#0b1220',
  menuLabel: '#94a3b8',
  menuLabelActive: '#f1f5f9',
};

const HOME_LIGHT: HomePalette = {
  pageBackground: '#fafafa',
  headerBackground: '#1F2937',
  greeting: '#FFFFFF',
  welcomeText: '#E0F2F7',
  cardBackground: '#FFFFFF',
  cardBorder: '#F4F4F4',
  cardShadowColor: '#000000',
  cardShadowOpacity: 0.08,
  sectionTitle: '#222222',
  sectionArrow: '#374151',
  checkboxLabel: '#374151',
  textSecondary: '#374151',
  textMuted: '#6B7280',
  separator: '#E5E7EB',
  iconBackground: '#E0F2FE',
  iconForeground: '#0C4A6E',
  tableHeaderBackground: '#F3F4F6',
  tableRowAlternate: '#F9FAFB',
  exposureBarTrack: '#CBD5E1',
};

const HOME_DARK: HomePalette = {
  pageBackground: '#0f172a',
  headerBackground: '#1e293b',
  greeting: '#f1f5f9',
  welcomeText: '#94a3b8',
  cardBackground: '#1e293b',
  cardBorder: 'rgba(255,255,255,0.05)',
  cardShadowColor: '#000000',
  cardShadowOpacity: 0.4,
  sectionTitle: '#f1f5f9',
  sectionArrow: '#94a3b8',
  checkboxLabel: '#94a3b8',
  textSecondary: '#e5e7eb',
  textMuted: '#94a3b8',
  separator: 'rgba(255,255,255,0.06)',
  iconBackground: '#0b1220',
  iconForeground: '#93c5fd',
  tableHeaderBackground: '#0b1220',
  tableRowAlternate: '#0b1220',
  exposureBarTrack: 'rgba(148,163,184,0.35)',
};

const MODAL_DARK: ProfilePhotoModalPalette = {
  overlay: 'rgba(0, 0, 0, 0.62)',
  sheetBackground: '#1e293b',
  sheetBorder: 'rgba(255,255,255,0.05)',
  shadowColor: '#000000',
  title: '#F1F5F9',
  subtitle: '#94a3b8',
  guidelinesBackground: '#0b1220',
  guidelinesBorder: 'rgba(255,255,255,0.08)',
  guidelinesTitle: '#f1f5f9',
  guidelinesLine: '#94a3b8',
  chooseButton: '#2563EB',
  chooseButtonText: '#FFFFFF',
  activityOnChoose: '#FFFFFF',
  previewRing: 'rgba(255,255,255,0.08)',
  previewMeta: '#94a3b8',
  errorText: '#FCA5A5',
  successText: '#6EE7B7',
  footerBorder: 'rgba(255,255,255,0.06)',
  cancelBorder: 'rgba(255,255,255,0.08)',
  cancelBackground: '#0b1220',
  cancelText: '#e5e7eb',
  acceptBackground: '#2563eb',
  acceptText: '#FFFFFF',
  doneBackground: '#1d4ed8',
  doneText: '#FFFFFF',
};

export function getAppearanceTokens(theme: AppTheme): AppearanceTokens {
  if (theme === 'dark') {
    return {
      settings: SETTINGS_DARK,
      modalProfilePhoto: MODAL_DARK,
      header: HEADER_DARK,
      topBarProfile: TOP_BAR_PROFILE_DARK,
      sidebar: SIDEBAR_DARK,
      home: HOME_DARK,
    };
  }
  return {
    settings: SETTINGS_LIGHT,
    modalProfilePhoto: MODAL_LIGHT,
    header: HEADER_LIGHT,
    topBarProfile: TOP_BAR_PROFILE_LIGHT,
    sidebar: SIDEBAR_LIGHT,
    home: HOME_LIGHT,
  };
}
